import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, MessageSquare, MessageCircle, Mail, Headphones } from 'lucide-react';
import Gleap from 'gleap';
import { ProfileAvatar } from '@/components/ProfileAvatar';

const SUPPORT_NUMBER = '+18886291612';

const supportActions = [
  {
    id: 'chat',
    title: 'Chat with Us',
    subtitle: 'Talk to support directly',
    icon: Headphones,
    gradient: 'from-orange-500 to-amber-600',
    onClick: () => Gleap.open(),
    disabled: false,
  },
  {
    id: 'call',
    title: 'Call',
    subtitle: 'Speak with a support agent',
    icon: Phone,
    gradient: 'from-green-500 to-emerald-600',
    href: `tel:${SUPPORT_NUMBER}`,
    disabled: true,
  },
  {
    id: 'text',
    title: 'Text',
    subtitle: 'Send us a text message',
    icon: MessageSquare,
    gradient: 'from-blue-500 to-cyan-600',
    href: `sms:${SUPPORT_NUMBER}`,
    disabled: true,
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp',
    subtitle: 'Message us on WhatsApp',
    icon: MessageCircle,
    gradient: 'from-emerald-500 to-green-600',
    href: `https://wa.me/18886291612`,
    disabled: false,
  },
  {
    id: 'email',
    title: 'Email',
    subtitle: 'Send us an email',
    icon: Mail,
    gradient: 'from-purple-500 to-violet-600',
    href: 'mailto:support@shomri.com',
    disabled: true,
  },
];

const Support: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Shomri Support</h1>
              <p className="text-[13px] text-muted-foreground">We're here to help</p>
            </div>
          </div>
          <ProfileAvatar />
        </div>
      </div>

      <div className="px-4 pt-6">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3 px-1">
          Contact Us
        </p>
        <div className="space-y-3">
          {supportActions.map((action) => {
            const Icon = action.icon;
            const isClickAction = 'onClick' in action && action.onClick;
            const isDisabled = action.disabled;

            const className = `block w-full bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] p-4 transition-all duration-200 ${
              isDisabled
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:shadow-lg active:scale-[0.98] group'
            }`;

            const content = (
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center shadow-lg`}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[15px] text-foreground">{action.title}</p>
                  <p className="text-[13px] text-muted-foreground">{action.subtitle}</p>
                </div>
                {isDisabled && (
                  <span className="text-[11px] text-muted-foreground font-medium">Coming soon</span>
                )}
              </div>
            );

            if (isDisabled) {
              return <div key={action.id} className={className}>{content}</div>;
            }
            if (isClickAction) {
              return <button key={action.id} onClick={action.onClick} className={`${className} text-left`}>{content}</button>;
            }
            return <a key={action.id} href={action.href} className={className}>{content}</a>;
          })}
        </div>
      </div>
    </div>
  );
};

export default Support;
